import { Building2Icon, FileImageIcon, UploadIcon, XIcon } from 'lucide-react';
import { type ChangeEvent, type DragEvent, type FC, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui/input';
import { ACCEPTED_FLOORPLANS_INPUT_TYPE } from '@/features/estate-media/schema/upload-media';
import { cn } from '@/lib/utils';

import type { EstateProjectSearchItem } from '../types';
import { formatFileSize, getFileKey } from '../utils/floor-plan-file';

type UploadStepProps = {
  files: File[];
  isUploading: boolean;
  selectedItem: EstateProjectSearchItem;
  onAddFiles: (files: File[]) => void;
  onChangeSelection: () => void;
  onRemoveFile: (fileKey: string) => void;
};

export const UploadStep: FC<UploadStepProps> = ({
  files,
  isUploading,
  selectedItem,
  onAddFiles,
  onChangeSelection,
  onRemoveFile,
}) => {
  const { t } = useTranslation();
  const fileInputId = useId();
  const [isDragging, setIsDragging] = useState(false);

  const fileInputChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    onAddFiles(Array.from(event.target.files ?? []));
    event.target.value = '';
  };

  const handleDragOver = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    if (!isUploading) setIsDragging(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLLabelElement>) => {
    if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) {
      return;
    }

    setIsDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragging(false);

    if (!isUploading) {
      onAddFiles(Array.from(event.dataTransfer.files ?? []));
    }
  };

  return (
    <div className="min-h-0 flex-1 px-6 pb-4">
      <div className="flex h-full flex-col gap-4">
        <div className="flex h-16 items-center gap-3 rounded-md border border-slate-200 px-4 text-primary-dark">
          <Building2Icon className="size-5 shrink-0 text-slate-500" />
          <p className="min-w-0 flex-1 truncate text-base font-medium">
            {t('uploadingTo')}: <span className="font-bold">{selectedItem.name}</span>
          </p>
          <button
            type="button"
            className="text-base font-bold text-primary-dark transition-colors hover:text-primary-light disabled:pointer-events-none disabled:opacity-50"
            disabled={isUploading}
            onClick={onChangeSelection}
          >
            {t('change')}
          </button>
        </div>

        <label
          htmlFor={fileInputId}
          className={cn(
            'flex min-h-44 cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed border-slate-200 px-6 text-center transition-colors hover:border-primary-light',
            isDragging && 'border-primary-light bg-primary-lighter/20',
            isUploading && 'pointer-events-none opacity-60'
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <UploadIcon className="mb-5 size-12 text-slate-500" />
          <p className="text-base font-medium text-slate-500">{t('dragDropFloorPlan')}</p>
          <p className="mt-3 text-sm font-medium text-slate-400">{t('supportsJpgPngPdf')}</p>
          <Input
            id={fileInputId}
            type="file"
            accept={ACCEPTED_FLOORPLANS_INPUT_TYPE}
            className="hidden"
            disabled={isUploading}
            multiple
            onChange={fileInputChangeHandler}
          />
        </label>

        {files.length > 0 ? (
          <div className="min-h-0 space-y-2 overflow-y-auto pr-1">
            {files.map((file) => (
              <div
                key={getFileKey(file)}
                className="flex h-16 items-center gap-3 rounded-md border border-slate-200 px-4"
              >
                <FileImageIcon className="size-8 shrink-0 text-primary-light" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-base font-bold text-primary-dark">{file.name}</p>
                  <p className="text-sm font-medium text-slate-500">{formatFileSize(file.size)}</p>
                </div>
                <button
                  type="button"
                  className="rounded-full p-1 text-primary-dark transition-colors hover:bg-slate-100 disabled:pointer-events-none disabled:opacity-50"
                  disabled={isUploading}
                  onClick={() => onRemoveFile(getFileKey(file))}
                  aria-label={`${t('remove')} ${file.name}`}
                >
                  <XIcon className="size-5" />
                </button>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};
